import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { applyAudioSink, log } from '~/helpers';
import muteTone from '~/assets/sounds/muteTone.wav';
import unmuteTone from '~/assets/sounds/unmuteTone.wav';

/**
 * The two confirmation tones, keyed by the action being taken.
 *
 * Both are 140ms of 16-bit mono 44.1kHz: two 70ms segments a fifth apart (660Hz and 440Hz) with a
 * 4ms fade on each end so the edges do not click. Mute FALLS in pitch and unmute RISES, which is
 * what makes them tellable apart - pitch alone is hard to judge without hearing them back to back,
 * but direction is obvious from a single press and survives a cheap headset.
 */
const TONES = {
  mute: muteTone,
  unmute: unmuteTone,
};

/**
 * @name useMuteTone
 * @description Play a short local confirmation tone when the user mutes or unmutes.
 *
 * "Local" is structural rather than something this has to be careful about: an `Audio` element is
 * never attached to a peer connection, so the far end cannot hear it. The two places that DO push
 * an element into the call - `conferenceMixer` and the announcement player - build their graphs
 * from `MediaStreamTrack`s, and nothing here goes near them. The only path to the far end is
 * acoustic, through the user's own microphone, which is what echo cancellation is for.
 *
 * Routed to the resolved CALL SPEAKER, so it lands in the headset the user is talking into rather
 * than the laptop speakers. Deliberately the resolved `deviceManager.audio.speaker` and not the
 * `system.ringDevice` pin: a pin survives its device being unplugged, and playing through a stale
 * one throws "Requested device not found" (the same trap documented in `Sounds` and `PlayTest`).
 *
 * The elements are built once and kept, rather than per press: `setSinkId` is async, so creating
 * and routing an element inside the click would put the round trip in front of the sound the press
 * is meant to confirm. Here the sink is already applied and the press only has to seek and play.
 * @returns {(action: 'mute' | 'unmute') => void}
 */
export default function useMuteTone() {
  const speakerId = useSelector(
    state => state.system?.deviceManager?.audio?.speaker?.deviceId
  );

  const players = useRef(null);

  if (!players.current) {
    players.current = {
      mute: new Audio(TONES.mute),
      unmute: new Audio(TONES.unmute),
    };
  }

  // Re-applied whenever the speaker moves. `applyAudioSink` resolves to false rather than throwing
  // on Firefox and Safari, which have no setSinkId - there the tones follow the OS default, which
  // is the right behaviour when the browser exposes no way to choose.
  useEffect(() => {
    if (!speakerId) {
      return;
    }

    Object.values(players.current).forEach(player => {
      applyAudioSink(player, speakerId);
    });
  }, [speakerId]);

  return useCallback(action => {
    const player = players.current?.[action];

    if (!player) {
      return;
    }

    // Seek back, so a second press during the first tone restarts it instead of being ignored.
    player.currentTime = 0;
    player
      .play()
      .catch(err => log.error(`Failed to play the ${action} tone.`, err));
  }, []);
}
